import type { Email } from '@/types/email';
import type { UserColumn, FilterRule, ColumnFilters } from '@/types/column';

/**
 * Check if an email matches all filters of a column
 */
export function emailMatchesColumn(email: Email, column: UserColumn): boolean {
  if (!column.isVisible) return false;
  return emailMatchesFilters(email, column.filters);
}

/**
 * Check if an email matches a set of filters
 */
export function emailMatchesFilters(email: Email, filters: ColumnFilters): boolean {
  if (filters.rules.length === 0) return false;

  if (filters.logic === 'AND') {
    return filters.rules.every(rule => emailMatchesRule(email, rule));
  } else {
    return filters.rules.some(rule => emailMatchesRule(email, rule));
  }
}

/**
 * Check if an email matches a single filter rule
 */
export function emailMatchesRule(email: Email, rule: FilterRule): boolean {
  const { type, operator, value } = rule;

  switch (type) {
    case 'label':
      return matchLabels(email, operator, value);

    case 'sender':
      return matchSender(email, operator, value);

    case 'domain':
      return matchDomain(email, operator, value);

    case 'keyword':
      return matchKeyword(email, operator, value);

    case 'priority':
      return matchPriority(email, operator, value);

    case 'hasAttachments':
      return matchHasAttachments(email, operator, value);

    case 'isUnread':
      return matchIsUnread(email, operator, value);

    case 'hasThread':
      return matchHasThread(email, operator, value);

    case 'category':
      return matchCategory(email, operator, value);

    case 'receivedWithin':
      return matchReceivedWithin(email, operator, value);

    case 'recipient':
      return matchRecipient(email, operator, value);

    default:
      return false;
  }
}

// ============================================
// Matcher Functions
// ============================================

function matchLabels(email: Email, operator: string, value: string | string[] | boolean): boolean {
  // Check both AI labels and user-assigned labels
  const aiLabels = email.aiAnalysis?.labels || [];
  const userLabels = email.userLabels?.map(l => l.name) || [];
  const allLabels = [...aiLabels, ...userLabels];

  const targetLabels = Array.isArray(value) ? value : [value as string];

  const hasMatch = targetLabels.some(label =>
    allLabels.some(l => l.toLowerCase() === label.toLowerCase())
  );

  return operator === 'isNot' ? !hasMatch : hasMatch;
}

function matchSender(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const senderLower = email.sender.toLowerCase();
  const valueLower = (value as string).toLowerCase();

  if (operator === 'contains') {
    return senderLower.includes(valueLower);
  }

  const matches = senderLower === valueLower || senderLower.includes(`<${valueLower}>`);
  return operator === 'isNot' ? !matches : matches;
}

function matchDomain(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const senderLower = email.sender.toLowerCase();
  const domain = (value as string).toLowerCase();

  // Extract domain from sender (e.g., "John <john@example.com>" -> "example.com")
  const domainMatch = senderLower.match(/@([a-z0-9.-]+)/i);
  const senderDomain = domainMatch ? domainMatch[1] : '';

  const matches = senderDomain === domain || senderDomain.endsWith(`.${domain}`);
  return operator === 'isNot' ? !matches : matches;
}

function matchKeyword(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const keyword = (value as string).toLowerCase();
  const searchText = `${email.subject} ${email.snippet}`.toLowerCase();

  const matches = searchText.includes(keyword);
  return operator === 'isNot' ? !matches : matches;
}

function matchPriority(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const emailPriority = email.aiAnalysis?.priority;
  if (!emailPriority) return operator === 'isNot';

  const targetPriorities = Array.isArray(value) ? value : [value as string];
  const matches = targetPriorities.includes(emailPriority);

  return operator === 'isNot' ? !matches : matches;
}

function matchHasAttachments(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const targetValue = value === true || value === 'true';
  const matches = email.hasAttachments === targetValue;

  return operator === 'isNot' ? !matches : matches;
}

function matchIsUnread(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const targetValue = value === true || value === 'true';
  const isUnread = !email.isRead;
  const matches = isUnread === targetValue;

  return operator === 'isNot' ? !matches : matches;
}

function matchHasThread(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const targetValue = value === true || value === 'true';
  const hasThread = (email.threadCount || 1) > 1;
  const matches = hasThread === targetValue;

  return operator === 'isNot' ? !matches : matches;
}

function matchCategory(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const emailCategory = email.aiAnalysis?.category;
  if (!emailCategory) return operator === 'isNot';

  const targetCategories = Array.isArray(value) ? value : [value as string];
  const matches = targetCategories.includes(emailCategory);

  return operator === 'isNot' ? !matches : matches;
}

function matchReceivedWithin(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const emailDate = new Date(email.receivedAt).getTime();
  const now = Date.now();
  const days = parseInt(value as string, 10);

  if (isNaN(days)) return false;

  const cutoff = now - (days * 24 * 60 * 60 * 1000);
  const isWithin = emailDate >= cutoff;

  return operator === 'isNot' ? !isWithin : isWithin;
}

function matchRecipient(email: Email, operator: string, value: string | string[] | boolean): boolean {
  const recipientLower = (email.recipient || '').toLowerCase();
  if (!recipientLower) return operator === 'isNot';

  const valueLower = (value as string).toLowerCase();

  if (operator === 'contains') {
    return recipientLower.includes(valueLower);
  }

  const matches = recipientLower === valueLower;
  return operator === 'isNot' ? !matches : matches;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Get emails for a specific column
 */
export function getEmailsForColumn(emails: Email[], column: UserColumn): Email[] {
  return emails.filter(email => emailMatchesColumn(email, column));
}

/**
 * Sort emails based on column settings
 */
export function sortEmailsForColumn(emails: Email[], column: UserColumn): Email[] {
  const sorted = [...emails];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (column.sortBy) {
      case 'date':
        comparison = new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
        break;
      case 'sender':
        comparison = a.sender.localeCompare(b.sender);
        break;
      case 'subject':
        comparison = a.subject.localeCompare(b.subject);
        break;
    }

    return column.sortDirection === 'desc' ? comparison : -comparison;
  });

  return sorted;
}

/**
 * Get emails for a column with sorting applied
 */
export function getColumnEmails(emails: Email[], column: UserColumn): Email[] {
  const filtered = getEmailsForColumn(emails, column);
  return sortEmailsForColumn(filtered, column);
}

/**
 * Count matching emails for preview
 */
export function countMatchingEmails(emails: Email[], filters: ColumnFilters): number {
  return emails.filter(email => emailMatchesFilters(email, filters)).length;
}
